import { SelectItem } from "@fronterahealth/frontera-ui-components";

import { ReportGoalType, ReportTargetType } from "@api/graphql/types-and-hooks";
import {
  CATALIGHT_GOAL_STATUS_SELECT_OPTIONS,
  GAS_SCORE_OPTIONS,
} from "@components/ShortTermGoalPanel/catalightConstants";

/**
 * Convert snake_case field_identifier to camelCase GraphQL field name.
 */
export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());
}

/**
 * Extract native goal field values as a snake_case keyed record.
 * Custom field values are handled internally by TreatmentPlanForm.
 */
export function goalToFieldValues(
  goal: Partial<
    Pick<
      ReportGoalType,
      | "goalName"
      | "masteryCriteria"
      | "baselineData"
      | "baselineDate"
      | "assessmentToolSource"
      | "projectedIntroductionDate"
      | "completionDate"
      | "generalizationCriteria"
      | "rationale"
      | "progressSummary"
      | "gasScore"
      | "gasLevel1"
      | "gasLevel3"
      | "gasLevel4"
      | "status"
    >
  >,
  defaultEmpty: string | null = null,
): Record<string, string | null> {
  const d = defaultEmpty;
  // Native date/datetime inputs reject the "-" placeholder, so blank date fields.
  const dateD = defaultEmpty === "-" ? "" : d;
  return {
    goal_name: goal.goalName ?? d,
    mastery_criteria: goal.masteryCriteria ?? d,
    baseline_data: goal.baselineData ?? d,
    baseline_date: goal.baselineDate ?? dateD,
    assessment_tool_source: goal.assessmentToolSource ?? d,
    projected_introduction_date: goal.projectedIntroductionDate ?? dateD,
    completion_date: goal.completionDate ?? dateD,
    generalization_criteria: goal.generalizationCriteria ?? d,
    rationale: goal.rationale ?? d,
    progress_summary: goal.progressSummary ?? d,
    gas_score: goal.gasScore ?? d,
    gas_level_1: goal.gasLevel1 ?? d,
    gas_level_3: goal.gasLevel3 ?? d,
    gas_level_4: goal.gasLevel4 ?? d,
    status: goal.status ?? d,
  };
}

/**
 * Extract native target field values as a snake_case keyed record.
 * Custom field values are handled internally by TreatmentPlanForm.
 */
export function targetToFieldValues(
  target: Partial<
    Pick<
      ReportTargetType,
      | "targetName"
      | "masteryCriteria"
      | "baselineData"
      | "completionDate"
      | "progressSummary"
      | "status"
      | "projectedIntroductionDate"
    >
  >,
  defaultEmpty: string | null = null,
): Record<string, string | null> {
  const d = defaultEmpty;
  // Native date/datetime inputs reject the "-" placeholder, so blank date fields.
  const dateD = defaultEmpty === "-" ? "" : d;
  return {
    target_name: target.targetName ?? d,
    mastery_criteria: target.masteryCriteria ?? d,
    baseline_data: target.baselineData ?? d,
    completion_date: target.completionDate ?? dateD,
    progress_summary: target.progressSummary ?? d,
    status: target.status ?? d,
    projected_introduction_date: target.projectedIntroductionDate ?? dateD,
  };
}

// ── Select options fallbacks for field types ─────────────────────────────────────────────────

const FIELD_OPTIONS_MAP: Record<string, SelectItem[]> = {
  gas_score: GAS_SCORE_OPTIONS,
  status: CATALIGHT_GOAL_STATUS_SELECT_OPTIONS.map((item) => ({
    primary: item.label,
    id: item.id,
  })),
};

/**
 * Get select options for a field. Priority:
 * 1. Parse field.content JSON (expects { options: string[] })
 * 2. Fall back to static lookup by field_identifier
 */
export function getSelectOptionsForField(fieldIdentifier: string, fieldContent?: string | null): SelectItem[] {
  if (fieldContent) {
    try {
      const parsed = JSON.parse(fieldContent);
      if (Array.isArray(parsed?.options)) {
        return parsed.options.map(
          (item: string | { id?: string; primary?: string; label?: string; value?: string }) => {
            if (typeof item === "string") {
              return { primary: item, id: item };
            }
            return {
              primary: item.primary || item.label || item.value || String(item),
              id: item.id || item.value || item.primary || String(item),
            };
          },
        );
      }
    } catch {
      // not valid JSON, fall through
    }
  }

  return FIELD_OPTIONS_MAP[fieldIdentifier] || [];
}
